<div class="portal-page">

	<!-- Top bar -->
	<portal-header mode="section" [projectId]="projectId" current="progress" [sections]="sections"></portal-header>

	<portal-loader *ngIf="loading"></portal-loader>
	<div class="sec-state portal-error" *ngIf="error">{{ error }}</div>

	<div class="section-scroll" *ngIf="!loading && !error">

		<!-- Progress card -->
		<div class="pp-card">
			<div class="pp-card-lbl">Progress</div>
			<div class="pp-card-body d-flex align-items-start">
				<div class="pp-left d-flex flex-column">
					<div class="d-flex justify-content-between align-items-center gap-3">
						<div class="pp-status-heading" [style.color]="statusColor">{{ statusHeading }}</div>
						<div class="pp-pct-done">{{ progress }}% Done</div>
					</div>

					<div class="pp-subtitle">{{ statusSubtitle }}</div>

					<div class="pp-bars-area">
						<div class="pp-marker" *ngIf="expectedPct !== null" [style.left.%]="expectedPct"></div>
						<div class="pp-bars">
							<div class="pp-bar" *ngFor="let f of bars"
								[style.background]="f ? statusColor : 'var(--c-border-1)'"></div>
						</div>
					</div>

					<div class="pp-dates-row d-flex justify-content-between">
						<div class="d-flex flex-column gap-1">
							<span class="pp-date-lbl">Start date</span>
							<span class="pp-date-val">{{ (startDate | localizedDate) || '—' }}</span>
						</div>
						<div class="d-flex flex-column gap-1" style="text-align:right;">
							<span class="pp-date-lbl">Est. completion</span>
							<span class="pp-date-val">{{ (endDate | localizedDate) || '—' }}</span>
						</div>
					</div>
				</div>

				<div class="pp-right d-flex flex-column w-100">
					<div class="pp-stats-row d-flex flex-wrap">
						<div class="d-flex flex-column gap-1">
							<span class="pp-stat-lbl">Start date</span>
							<span class="pp-stat-val">{{ (startDate | localizedDate) || '—' }}</span>
						</div>
						<div class="d-flex flex-column gap-1">
							<span class="pp-stat-lbl">Est. completion</span>
							<span class="pp-stat-val">{{ (endDate | localizedDate) || '—' }}</span>
						</div>
						<div class="d-flex flex-column gap-1" *ngIf="daysElapsed">
							<span class="pp-stat-lbl">Days elapsed</span>
							<span class="pp-stat-val">{{ daysElapsed.elapsed }}<span *ngIf="daysElapsed.total"> / {{ daysElapsed.total }}</span></span>
						</div>
					</div>
					<div class="d-flex flex-column gap-1">
						<span class="pp-stat-lbl">Notes on project</span>
						<span class="pp-notes">{{ projectNotes }}</span>
					</div>
				</div>
			</div>
		</div>

		<!-- Your Attention Needed: quotes awaiting acceptance + invoices to pay -->
		<div class="pp-attention">
			<div class="pp-attention-header d-flex align-items-center justify-content-between">
				<span class="pp-attention-title">Your Attention Needed</span>
				<span class="pp-actions-badge" *ngIf="pendingCount">
					<i class="wq wq-alert"></i>
					{{ pendingCount }} {{ pendingCount === 1 ? 'Action' : 'Actions' }} Required
				</span>
			</div>

			<p class="portal-muted" *ngIf="!pendingCount" style="margin:0">
				You're all caught up — nothing needs your attention right now.
			</p>

			<!-- Quotes awaiting your acceptance -->
			<ng-container *ngIf="pendingQuotes.length">
				<div class="pp-section-label">Quotes</div>
				<div class="qr" *ngFor="let q of pendingQuotes">
					<div class="qr-thumb">
						<img class="qr-thumb-img" [src]="q.image_url || 'assets/image-placeholder.png'" alt="">
					</div>

					<div class="qr-name">
						<div class="qr-meta">
							<span class="qr-number">Quote #{{ q.quote_no }}</span>
							<span class="qr-revision" *ngIf="q.revision > 1 || q.revision_name">Revision <ng-container *ngIf="q.revision_name">{{ q.revision_name }} </ng-container>{{ q.revision }}</span>
						</div>
						<div class="qr-title">{{ q.title }}</div>
					</div>

					<div class="qr-status">
						<span class="badge" [ngClass]="badgeClass(q.status)">{{ badgeLabel(q.status) }}</span>
						<span class="qr-date" [ngClass]="quoteDateLabel(q).cls">{{ quoteDateLabel(q).text }}</span>
					</div>

					<div class="qr-payment"></div>

					<div class="qr-view">
						<portal-button (click)="openProposal(q)" [disabled]="!q.can_view_proposal" [title]="q.can_view_proposal ? null : 'No proposal published for this quote'">View Quote</portal-button>
					</div>

					<div class="qr-orig">
						<span class="qr-orig-val">{{ q.net_total | currency:q.currency_id }}</span>
						<span class="qr-orig-lbl">Total (excl. Tax)</span>
					</div>

					<div class="qr-actions" *ngIf="isPending(q)">
						<div class="portal-btn-group">
							<portal-button icon="wq wq-notes" [title]="preview.ready ? preview.blockedReason : (q.can_comment ? 'Add a comment' : 'No proposal published for this quote')" aria-label="Add a comment" (click)="commentQuote(q)" [disabled]="preview.ready || !q.can_comment"></portal-button>
							<portal-button *ngIf="canConfirm(q)" (click)="openToBeConfirmed(q)" [disabled]="preview.ready" [title]="preview.ready ? preview.blockedReason : null">To be confirmed</portal-button>
							<portal-button (click)="openReject(q)" [disabled]="preview.ready" [title]="preview.ready ? preview.blockedReason : null">Reject</portal-button>
							<portal-button (click)="acceptQuote(q)" [disabled]="preview.ready || !q.can_accept" [title]="preview.ready ? preview.blockedReason : (q.can_accept ? null : 'No proposal published for this quote')">Accept</portal-button>
						</div>
					</div>
				</div>
			</ng-container>

			<!-- Invoices still to be paid -->
			<ng-container *ngIf="unpaidInvoices.length">
				<div class="pp-section-label">Invoices</div>
				<div class="inv-row inv-grid" *ngFor="let inv of unpaidInvoices">
					<!-- Mobile-only status line -->
					<div class="inv-status-dot-row">
						<div class="inv-status-dot" style="background:var(--c-primary-solid);"></div>
						<span class="inv-status-label">Unpaid</span>
					</div>

					<!-- Col 1 — title + ref (amount stacked here on mobile) -->
					<div class="inv-title-row">
						<div class="inv-title-col">
							<div class="inv-title">{{ inv.description || 'Invoice' }}</div>
							<div class="inv-ref">Invoice #{{ inv.invoice_no }}</div>
						</div>
						<div class="inv-amount-col">
							<div class="inv-amount-val">{{ inv.subtotal | currency:inv.currency_id }}</div>
							<div class="inv-amount-lbl">exclude Tax</div>
						</div>
					</div>

					<!-- Col 3 — payment progress -->
					<div class="inv-payment">
						<div class="inv-payment-label">Payment Progress</div>
						<div class="inv-payment-amount-row">
							<div class="inv-payment-big">{{ inv.paid | currency:inv.currency_id }}</div>
							<div class="inv-payment-of">of {{ inv.subtotal | currency:inv.currency_id }} Paid so far</div>
						</div>
						<portal-progress-bar size="md" [paid]="inv.paid" [toPay]="inv.to_pay" [total]="inv.subtotal"></portal-progress-bar>
						<div class="inv-payment-legend">
							<div class="inv-legend-item">
								<div class="inv-legend-dot-row"><div class="inv-legend-dot" style="background:var(--c-success-solid);"></div> Paid so far <span class="inv-legend-pct">{{ invPct(inv.paid, inv.subtotal) | number:'1.0-0' }}%</span></div>
								<div class="inv-legend-val">{{ inv.paid | currency:inv.currency_id }}</div>
							</div>
							<div class="inv-legend-item right">
								<div class="inv-legend-dot-row"><div class="inv-legend-dot" style="background:var(--c-primary-solid);"></div> To pay now <span class="inv-legend-pct">{{ invPct(inv.to_pay, inv.subtotal) | number:'1.0-0' }}%</span></div>
								<div class="inv-legend-val">{{ inv.to_pay | currency:inv.currency_id }}</div>
							</div>
						</div>
					</div>

					<!-- Col 4 — actions -->
					<div class="inv-btns">
						<!-- Reachable in a staff preview — see the matching button on the invoices tab. -->
						<portal-button (click)="openInvoice(inv)" [disabled]="!inv.invoice_url" [title]="inv.invoice_url ? null : 'Invoice not available to view'">Invoice Details</portal-button>
						<portal-button (click)="payInvoice(inv)" [disabled]="preview.ready || !inv.pay_url" [title]="preview.ready ? preview.blockedReason : (inv.pay_url ? null : 'Online payment is not available for this invoice')">Pay now</portal-button>
					</div>
				</div>
			</ng-container>
		</div>

	</div>

	<portal-project-footer [projectId]="projectId"></portal-project-footer>

	<portal-to-be-confirmed-modal #tbcModal (close)="onToBeConfirmed($event)"></portal-to-be-confirmed-modal>
	<portal-reject-modal #rejectModal (close)="onRejected($event)"></portal-reject-modal>
</div>
